import api from './api';
import clientAIService from './clientAIService';

const LOCAL_ONLY = (process.env.REACT_APP_LOCAL_ONLY || 'true') === 'true';

class NLPService {
  constructor() {
    this.useClientAI = false; // Will be set based on backend availability
    this.localOnly = LOCAL_ONLY;
  }

  async translateToSQL(question, columns, tableName, schemaContext = null) {
    // Transport (ARCHITECTURE_V2): Browser -> AI Gateway -> Nebius Token Factory.
    // Only the question + column schema are sent -- raw rows never leave the browser.
    try {
      console.log('📤 Sending NLP request to AI Gateway:', { question, columns, tableName });

      const response = await api.post('/ai/generate-sql', {
        question,
        columns,
        table_name: tableName,
        schema_context: schemaContext
      });

      console.log('✅ AI Gateway response:', response.data);
      this.useClientAI = false;
      return response.data.sql;
    } catch (error) {
      console.warn('⚠️ AI Gateway unavailable, using offline rule-based SQL:', error.message);

      // Offline fallback: deterministic rule-based generation (no network, no rows).
      this.useClientAI = true;
      return clientAIService.generateRuleBasedSQL(question, columns, tableName);
    }
  }

  async generateNaturalAnswer(question, sql, results, rowCount) {
    try {
      if (this.localOnly) {
        return await clientAIService.generateAnswer(question, sql, results, rowCount);
      }

      // Try backend first if it was working
      if (!this.useClientAI) {
        const response = await api.post('/generate-answer', {
          question,
          sql_query: sql,
          results,
          row_count: rowCount
        });
        return response.data.answer;
      }
    } catch (error) {
      console.warn('⚠️ Backend answer generation failed, using client-side:', error.message);
    }
    
    // Use client-side AI
    return await clientAIService.generateAnswer(question, sql, results, rowCount);
  }

  /**
   * Check if client-side AI mode is active
   */
  isUsingClientAI() {
    return this.useClientAI;
  }

  /**
   * Check if local-only mode is enabled
   */
  isLocalOnly() {
    return this.localOnly;
  }

  /**
   * Force use of client-side AI
   */
  setClientAIMode(enabled) {
    this.useClientAI = enabled;
  }

  /**
   * Toggle local-only mode
   */
  setLocalOnly(enabled) {
    this.localOnly = enabled;
    if (enabled) {
      this.useClientAI = true;
    }
  }

  /**
   * Check backend health and set mode accordingly
   */
  async checkBackendHealth() {
    try {
      if (this.localOnly) {
        this.useClientAI = true;
        return { healthy: false, message: 'Local-only mode enabled' };
      }

      const response = await api.get('/health');
      this.useClientAI = false;
      return { healthy: true, message: response.data.message };
    } catch (error) {
      this.useClientAI = true;
      return { healthy: false, message: 'Backend unavailable, using client-side AI' };
    }
  }
}

const nlpService = new NLPService();
export default nlpService;
